import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialTable, {
  MTableAction,
  MTableToolbar,
  MTableBodyRow,
} from "material-table";
import { Paper } from "@material-ui/core";
import { Button } from "antd";
import CommonButton from "../others/common-button";
import { compareTwoObjs } from "../../../services/lib/utils/helpers";

export interface CommonTableTwoLineProps {
  title?: string;
  column: any;
  initData: any;
  editable?: boolean;
  updateData?: any;
  isNotDeletable?: boolean;
  isNotEditable?: boolean;
  isNotAddable?: boolean;
  isExportable?: boolean;
  actionButtons?: any;
  addRowButtonName?: string;
  notAllowUpdate?: boolean;
  setTableData?: any;
  headerFields?: any;
  isEnableSelect?: any;
  columnsButton?: any;
  onSelectionChange?: any;
  onFilterChange?: any;
  onSearchChange?: any;
  defaultPageSize?: number;
  defaultPageSizeOptions?: any;
  selectionProps?: any;
  rowStyle?: any;
  triggerData?: any;
  maxBodyHeight?: number | string; // 添加maxBodyHeight配置以支持固定表头
  stickyHeader?: boolean; // 添加stickyHeader配置
}

const filterRowActions = (actions: any[] = []) =>
  (actions || []).filter((action) => !action?.isFreeAction);

const renderRowActions = (actions: any[] = [], rowData: any) => {
  if (!actions.length) return null;

  // 过滤掉隐藏的按钮
  const visibleActions = actions.filter((action) => {
    if (typeof action.hidden === "function") {
      return !action.hidden(rowData);
    }
    return action.hidden !== true;
  });

  if (!visibleActions.length) return null;

  // 分离左侧按钮（如 Confirm）和右侧按钮
  const leftActions = visibleActions.filter((action) => action.alignLeft === true);
  const rightActions = visibleActions.filter((action) => action.alignLeft !== true);

  const renderButton = (action: any, idx: number) => {
    const handleClick = (event: any) => {
      event?.stopPropagation?.();
      if (typeof action.onClick === "function") {
        action.onClick(event, rowData);
      }
    };

    const label = action.buttonLabel || action.tooltip || "Action";
    const background = action.buttonColor || "#1976D2";
    const textColor = action.buttonTextColor || "#ffffff";
    const iconNode =
      typeof action.buttonIcon === "function"
        ? action.buttonIcon(rowData)
        : typeof action.icon === "function"
        ? action.icon(rowData)
        : undefined;

    return (
      <Button
        key={`${action.tooltip || "action"}-${idx}`}
        type="primary"
        icon={iconNode}
        onClick={handleClick}
        style={{
          background,
          borderColor: background,
          color: textColor,
          fontWeight: 500,
          padding: "0 16px",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <div
      style={{
        padding: "8px 24px 16px",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {leftActions.map((action, idx) => renderButton(action, idx))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {rightActions.map((action, idx) => renderButton(action, idx + leftActions.length))}
      </div>
    </div>
  );
};

/**
 * Sanitizes columns to prevent MaterialTable's DataManager.setColumns from throwing
 * "RangeError: Invalid string length" when calling Array.join on usedWidth.
 * 
 * This error occurs when:
 * 1. Columns array is corrupted or has circular references
 * 2. Columns array grows too large (> 100 columns)
 * 3. Column definitions contain non-serializable data
 * 4. tableData is persisted and causes issues
 */
const buildSafeColumns = (cols: any[] = []): any[] => {
  // Ensure input is an array
  if (!Array.isArray(cols)) {
    console.warn('buildSafeColumns: columns is not an array', typeof cols, cols);
    return [];
  }

  // Limit maximum column count to prevent join() from exceeding string length
  const MAX_COLUMNS = 50;
  const safeCols = cols.slice(0, MAX_COLUMNS);

  return safeCols
    .filter((col) => {
      // Filter out null/undefined/invalid columns
      if (!col || typeof col !== 'object') {
        return false;
      }
      return true;
    })
    .map((col: any, index: number) => {
      try {
        // Remove MaterialTable's internal tableData field
        // MaterialTable will recreate this itself
        const { tableData, columnDef, ...rest } = col;

        // Create a clean column definition with only serializable properties
        const cleanCol: any = {
          title: rest.title || '',
          field: rest.field || `field_${index}`,
          // Preserve essential properties
          ...(rest.width !== undefined && { width: typeof rest.width === 'number' ? rest.width : undefined }),
          ...(rest.hidden !== undefined && { hidden: rest.hidden }),
          ...(rest.defaultFilter !== undefined && { defaultFilter: rest.defaultFilter }),
          ...(rest.defaultGroupOrder !== undefined && { defaultGroupOrder: rest.defaultGroupOrder }),
          ...(rest.defaultGroupSort !== undefined && { defaultGroupSort: rest.defaultGroupSort }),
          ...(rest.lookup !== undefined && { lookup: rest.lookup }),
          ...(rest.type !== undefined && { type: rest.type }),
          ...(rest.align !== undefined && { align: rest.align }),
          ...(rest.sorting !== undefined && { sorting: rest.sorting }),
          ...(rest.filtering !== undefined && { filtering: rest.filtering }),
          ...(rest.grouping !== undefined && { grouping: rest.grouping }),
          ...(rest.headerStyle !== undefined && { headerStyle: rest.headerStyle }),
          ...(rest.cellStyle !== undefined && { cellStyle: rest.cellStyle }),
        };

        // Preserve render function if it exists (functions are allowed in MaterialTable)
        if (typeof rest.render === 'function') {
          cleanCol.render = rest.render;
        }
        if (typeof rest.customFilterAndSearch === 'function') {
          cleanCol.customFilterAndSearch = rest.customFilterAndSearch;
        }
        if (typeof rest.customSort === 'function') {
          cleanCol.customSort = rest.customSort;
        }

        // Ensure width is reasonable (prevent extremely large values)
        if (cleanCol.width && typeof cleanCol.width === 'string') {
          const widthMatch = cleanCol.width.match(/^(\d+(?:\.\d+)?)(px|%)?$/);
          if (widthMatch) {
            const numValue = parseFloat(widthMatch[1]);
            if (numValue > 10000) {
              // Cap width at 10000px to prevent issues
              cleanCol.width = '10000px';
            }
          }
        }

        return cleanCol;
      } catch (error) {
        console.error('buildSafeColumns: Error sanitizing column', index, col, error);
        // Return a minimal valid column if sanitization fails
        return {
          title: `Column ${index + 1}`,
          field: `field_${index}`,
        };
      }
    });
};

const CustomRow = ({ actions, ...rowProps }: any) => {
  const { columns } = rowProps;
  const colSpan = (columns?.length || 0) + 1; // +1 for selection column when enabled
  const actionContent = renderRowActions(actions, rowProps.data);

  return (
    <>
      <MTableBodyRow {...rowProps} />
      {actionContent ? (
        <tr className="mt-table-two-line-actions">
          <td colSpan={colSpan} style={{ borderBottom: "1px solid rgba(224, 224, 224, 1)" }}>
            {actionContent}
          </td>
        </tr>
      ) : null}
    </>
  );
};

const CommonTableTwoLine: React.FC<CommonTableTwoLineProps> = (props) => {
  const {
    column,
    initData,
    actionButtons = [],
    editable,
    isNotEditable,
    isNotAddable,
    isNotDeletable,
  } = props;

  const tableRef: any = useRef<any>();
  const [data, setData] = useState(initData || []);
  
  // Use useMemo to sanitize columns and prevent unnecessary recalculations
  // This also ensures columns are always clean before being passed to MaterialTable
  const sanitizedColumns = useMemo(() => {
    const safe = buildSafeColumns(column || []);
    if (safe.length !== (column?.length || 0)) {
      console.warn(
        `CommonTableTwoLine: Column count changed from ${column?.length || 0} to ${safe.length} after sanitization`
      );
    }
    return safe;
  }, [column]);
  
  const [columns, setColumns] = useState(() => sanitizedColumns);

  const rowActions = useMemo(() => filterRowActions(actionButtons), [actionButtons]);
  const toolbarActions = useMemo(
    () => (actionButtons || []).filter((action: any) => action?.isFreeAction),
    [actionButtons]
  );

  const isTableEditable = editable ? !isNotEditable : false;
  const isTableAddable = editable ? !isNotAddable : false;
  const isTableDeletable = editable ? !isNotDeletable : false;
  const addRowButtonTooltip = "Add";
  const addRowButtonName = props.addRowButtonName || "Add new";

  useEffect(() => {
    let cancelled = false;
    if (!cancelled) setData(initData || []);
    return () => {
      cancelled = true;
    };
  }, [initData]);

  // Update columns when sanitized columns change
  useEffect(() => {
    setColumns(sanitizedColumns);
  }, [sanitizedColumns]);

  // 检测屏幕尺寸，用于响应式设计
  const [isSmallScreen, setIsSmallScreen] = React.useState(window.innerWidth < 1024);
  
  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      width: '100%',
      overflowX: 'auto', // 允许横向滚动
      boxSizing: 'border-box'
    }}>
      <MaterialTable
        tableRef={tableRef}
        title={props.title}
        columns={columns}
        data={data}
        actions={toolbarActions}
        options={{
          toolbarButtonAlignment: "left",
          pageSizeOptions: props.defaultPageSizeOptions || [5, 10, 30, 50, 100],
          pageSize: props.defaultPageSize || 100,
          exportButton: props.isExportable || false,
          exportAllData: props.isExportable || false, // 导出全部数据，不仅仅是当前页
          emptyRowsWhenPaging: false,
          filtering: true,
          debounceInterval: 500,
          addRowPosition: "first",
          headerStyle: {
            fontSize: isSmallScreen ? "0.875rem" : "0.95rem", // 统一表头字体大小
            whiteSpace: 'nowrap' // 防止表头换行
          },
          // doubleHorizontalScroll: true, // Temporarily disabled to avoid Invalid string length error
          draggable: true,
          selection: props.isEnableSelect,
          paginationType: "stepped",
          selectionProps: props.selectionProps,
          rowStyle: props.rowStyle,
          columnsButton: props.columnsButton,
          actionsColumnIndex: -1,
          // 添加固定表头支持
          ...(props.maxBodyHeight && { maxBodyHeight: props.maxBodyHeight }),
          ...(props.stickyHeader && { maxBodyHeight: props.maxBodyHeight || 'calc(100vh - 250px)' }),
        }}
        onSelectionChange={(rows) => {
          props.onSelectionChange?.(rows);
        }}
        onFilterChange={() => {
          props.onFilterChange?.(tableRef);
        }}
        onSearchChange={() => {
          props.onSearchChange?.(tableRef);
        }}
        onChangePage={() => {
          window.scrollTo(0, 0);
        }}
        localization={{
          header: {
            actions: editable ? "Actions" : "",
          },
          body: {
            addTooltip: addRowButtonTooltip,
          },
        }}
        components={{
          Container: (containerProps) => (
            <Paper
              {...containerProps}
              elevation={0}
              style={{ 
                border: "1px solid #e2e2e2",
                width: '100%',
                overflowX: 'auto', // 允许表格横向滚动
                boxSizing: 'border-box'
              }}
            />
          ),
          Action: (propsAction: any) => {
            const onBnClick = (event: any) =>
              propsAction.action.onClick(event, propsAction.data);
            const bnProps: any = { onClick: (event: any) => onBnClick(event) };
            for (let i = 0; i < toolbarActions.length; i++) {
              if (propsAction.action.tooltip === toolbarActions[i].tooltip) {
                const iconKey = typeof toolbarActions[i].icon === "function" ? "block" : (toolbarActions[i].icon || "block");
                const restProps = { ...bnProps, [iconKey]: true };
                return (
                  <CommonButton
                    name={toolbarActions[i].tooltip}
                    restProps={restProps}
                  />
                );
              }
            }
            if (propsAction.action.tooltip === addRowButtonTooltip) {
              const addBnProps: any = { ...bnProps };
              const state = tableRef.current?.state;
              if (!isTableAddable || !!state?.showAddRow || !!state?.lastEditingRow) {
                addBnProps.style = { display: "none" };
              }
              return <CommonButton name={addRowButtonName} restProps={addBnProps} />;
            }
            return <MTableAction {...propsAction} />;
          },
          Toolbar: (toolbarProps) => {
            return (
              <div>
                <MTableToolbar {...toolbarProps} />
                <div className="d-flex">
                  {props.headerFields &&
                    props.headerFields.map((dataField: any, key: any) => (
                      <div className="px-4" key={key}>
                        {dataField.click ? (
                          <a onClick={() => dataField.click(dataField.name)}> {dataField.name} </a>
                        ) : (
                          dataField.name
                        )}
                      </div>
                    ))}
                </div>
              </div>
            );
          },
          Row: (rowProps) => <CustomRow {...rowProps} actions={rowActions} />,
        }}
        editable={{
          isEditHidden: () => !isTableEditable,
          isDeleteHidden: () => !isTableDeletable,
          isEditable: () => isTableEditable,
          isDeletable: () => isTableDeletable,
          onRowAdd: (newData) =>
            new Promise((resolve, reject) => {
              if (!props.notAllowUpdate) {
                props
                  .updateData?.([...data, newData], newData, "create")
                  ?.then(() => resolve(""))
                  ?.catch(() => reject(""));
              } else {
                if (!data) {
                  props.setTableData?.([newData]);
                } else {
                  props.setTableData?.([...data, newData]);
                }
                resolve("");
              }
            }),
          onRowDelete: (oldData: any) =>
            new Promise((resolve, reject) => {
              const dataDelete = [...data];
              const index = oldData.tableData.id;
              dataDelete.splice(index, 1);
              if (!props.notAllowUpdate) {
                props
                  .updateData?.([...dataDelete], oldData, "delete")
                  ?.then(() => {
                    setData([...dataDelete]);
                    resolve("");
                  })
                  ?.catch(() => reject(""));
              } else {
                props.setTableData?.(dataDelete);
                resolve("");
              }
            }),
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              if (!oldData) return reject("");
              const isNothingChange = compareTwoObjs(
                { ...newData, tableData: null },
                { ...oldData, tableData: null }
              );
              if (isNothingChange) {
                reject("");
              } else {
                const dataUpdate = [...data];
                const index = oldData.tableData.id;
                dataUpdate[index] = newData;
                if (!props.notAllowUpdate) {
                  props
                    .updateData?.([...dataUpdate], newData, "update")
                    ?.then(() => {
                      setData([...dataUpdate]);
                      resolve("");
                    })
                    ?.catch(() => reject(""));
                } else {
                  props.setTableData?.(dataUpdate);
                  resolve("");
                }
              }
            }),
        }}
      />
    </div>
  );
};

export default CommonTableTwoLine;

